import type { ChatMessage } from "store/chatbotSlice";
import type { ICreateChatbotReviewCaseRequest } from "api/chatbot/chatbot.type";

/**
 * Builds the snapshot payload for one assistant turn (REF-6217 P1-0).
 *
 * Split out of `DashboardChatbot` on purpose: "did we capture the turn the reviewer
 * actually clicked" is the one thing about this feature that must be provable, and a
 * pure function over the message list is testable in a way an inline closure is not.
 *
 * Nothing here reads or writes network state — the caller owns the request. This module
 * exists only to answer "which question belongs to this answer".
 */

/** Conservative — the BE accepts 80. Evidence needs context, not the whole archive. */
export const MAX_CAPTURED_MESSAGES = 40;

export interface BuildReviewCaptureOptions {
  pageRoute: string;
  uiLocale?: string;
}

/**
 * Pair the selected assistant bubble with the question that produced it.
 *
 * Matching is by `sessionId` first, because that is the only field that actually ties a
 * question to its answer: a fresh `session_id` is minted per turn in `sendMessage`, and
 * both bubbles of that turn carry it. Position is the fallback for bubbles that carry no
 * session (the intro), where the nearest preceding user message is the best available
 * answer — a fallback, never the primary rule, since two turns in a row would otherwise
 * be indistinguishable.
 */
const findQuestionFor = (messages: ChatMessage[], index: number): ChatMessage | null => {
  const answer = messages[index];

  if (answer.sessionId) {
    for (let i = index - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "user" && m.sessionId === answer.sessionId) return m;
    }
  }

  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i].role === "user") return messages[i];
  }

  return null;
};

/**
 * Returns the capture payload, or `null` when this bubble cannot be reviewed.
 *
 * Null cases are deliberate rather than errors: the intro bubble is our own copy (there is
 * no question behind it), a still-streaming bubble has no final answer yet, and an empty
 * answer has nothing to correct. The caller renders no affordance in any of them.
 */
export const buildReviewCapture = (messages: ChatMessage[], assistantMessageId: string, options: BuildReviewCaptureOptions): ICreateChatbotReviewCaseRequest | null => {
  const index = messages.findIndex((m) => m.id === assistantMessageId);
  if (index < 0) return null;

  const answer = messages[index];
  if (answer.role !== "assistant" || answer.isIntro) return null;
  if (answer.status === "streaming") return null;
  if (!answer.text.trim()) return null;

  const question = findQuestionFor(messages, index);
  if (!question || !question.text.trim()) return null;

  // Context ends at the selected answer: anything after it is a later turn and did not
  // inform this one, so including it would make the evidence read as if it had.
  const transcript = messages.slice(0, index + 1).slice(-MAX_CAPTURED_MESSAGES);

  return {
    question: question.text,
    answer: answer.text,
    session_id: answer.sessionId,
    answer_status: answer.status,
    // The capability the answer routed to, if any. First action is the primary destination.
    nav_capability: answer.actions?.[0]?.capability_id ?? null,
    actions: answer.actions ?? [],
    page_route: options.pageRoute,
    ui_locale: options.uiLocale,
    transcript: transcript.map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text,
      status: m.status,
      session_id: m.sessionId,
    })),
  };
};

/** Can this bubble be captured at all? Drives whether the affordance renders. */
export const isReviewable = (messages: ChatMessage[], assistantMessageId: string): boolean => buildReviewCapture(messages, assistantMessageId, { pageRoute: "" }) !== null;
